import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import { Team, User } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { capitalize, toUnixSeconds } from "~/helpers/helpers";

const planLabel = (team: Team) => {
  const name = team.subscription?.price?.product?.name;
  const interval = team.subscription?.price?.recurring_interval;

  return name ? (interval ? `${name} ${capitalize(interval)}` : name) : "free";
};

export function IntercomGate({ user, team }: { user: User; team: Team }) {
  const { boot, update, shutdown } = useIntercom();
  const { pathname } = useLocation();
  const bootedRef = useRef(false);
  const isIntercomEnabled =
    team?.subscription?.price?.product?.priority_support;
  const allow = !!user && !!team && isIntercomEnabled;

  const { data } = useQuery({
    // on backend intercom_user_jwt contains cs_admin, user_id and user email
    // so, user email is a required dependency here
    queryKey: ["intercom", team?.id, user?.id, user?.email],
    enabled: !!user && !!team,
    queryFn: async () => {
      const response = await apiClient.user.intercomJwtList();

      return response.data?.user_intercom;
    },
  });

  useEffect(() => {
    const run = async () => {
      // shutdown if there is no user or the plan does not support Intercom
      if (!allow) {
        if (bootedRef.current) {
          shutdown();
          bootedRef.current = false;
        }

        return;
      }

      if (!user) return null;
      if (!team) return null;
      if (!data) return;

      const intercomData = {
        user_id: String(user.id),
        name: [user.first_name, user.last_name].filter(Boolean).join(" "),
        team: team.name,
        intercom_user_jwt: data.intercom_user_jwt,
        created_at: toUnixSeconds(user.created_at),
        plan: planLabel(team),
      };

      if (!bootedRef.current) {
        boot(intercomData);
        bootedRef.current = true;
      } else {
        update(intercomData);
      }
    };

    run();
  }, [data, allow, user, team, boot, update, shutdown]);

  useEffect(() => {
    if (bootedRef.current) update();
  }, [pathname, update]);

  return null;
}
